import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import Seo from '../components/Seo'
import { useShared } from '../lib/hooks'

export default function Cart() {
  const { cart, auth } = useShared()
  const [code, setCode] = useState('')

  function remove(id: number) {
    router.delete(`/cart/remove/${id}`, { preserveScroll: true })
  }

  function applyCode(e: React.FormEvent) {
    e.preventDefault()
    if (!code.trim()) return
    router.post('/cart/discount', { code }, { preserveScroll: true, onSuccess: () => setCode('') })
  }

  function removeCode() {
    router.delete('/cart/discount', { preserveScroll: true })
  }

  return (
    <>
      <Seo title="Cart" noindex />

      <div className="mx-auto max-w-3xl px-4 sm:px-6 py-12">
        <h1 className="text-3xl font-semibold tracking-tight">Your cart</h1>

        {cart.count === 0 ? (
          <div className="mt-10 border border-line p-10 text-center">
            <p className="text-muted">Your cart is empty.</p>
            <Link
              href="/packs"
              className="inline-grid place-items-center mt-6 h-11 px-6 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition"
            >
              Browse catalog →
            </Link>
          </div>
        ) : (
          <>
            <ul className="mt-8 border-t border-line">
              {cart.items.map((item) => (
                <li key={item.id} className="flex items-center gap-4 py-4 border-b border-line">
                  <div className="size-14 shrink-0 bg-ink-soft border border-line overflow-hidden">
                    {item.image && <img src={item.image} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link href={`/packs/${item.slug}`} className="font-medium hover:text-paper">{item.name}</Link>
                    <div className="mono-label mt-0.5">Preset Pack</div>
                  </div>
                  <div className="tabular-nums">{item.price}</div>
                  <button
                    type="button"
                    onClick={() => remove(item.id)}
                    className="mono-label hover:text-paper transition-colors"
                    aria-label={`Remove ${item.name}`}
                  >
                    Remove
                  </button>
                </li>
              ))}
            </ul>

            {/* Promo code */}
            <div className="mt-6">
              {cart.discount?.code ? (
                <div className="flex items-center justify-between border border-line bg-ink-soft px-4 py-3">
                  <span className="text-sm">
                    <span className="mono-label !text-paper">{cart.discount.code}</span>{' '}
                    <span className="text-muted">applied — {cart.discount.label}</span>
                  </span>
                  <button type="button" onClick={removeCode} className="mono-label hover:text-paper transition-colors">
                    Remove
                  </button>
                </div>
              ) : (
                <form onSubmit={applyCode} className="flex gap-2">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Discount code"
                    aria-label="Discount code"
                    className="flex-1 h-11 bg-ink-soft border border-line focus:border-paper outline-none px-3.5 text-paper uppercase tracking-wide text-sm transition-colors"
                  />
                  <button
                    type="submit"
                    className="h-11 px-5 border border-line text-sm hover:border-line-2 transition disabled:opacity-50"
                    disabled={!code.trim()}
                  >
                    Apply
                  </button>
                </form>
              )}
              {cart.discount_error && !cart.discount?.code && (
                <p className="mt-2 text-sm text-red-400">{cart.discount_error}</p>
              )}
            </div>

            {/* Totals */}
            <div className="mt-6 border-t border-line pt-4 space-y-2">
              <div className="flex items-center justify-between text-sm text-muted">
                <span className="mono-label">Subtotal</span>
                <span className="tabular-nums">{cart.subtotal}</span>
              </div>
              {cart.discount && (
                <div className="flex items-center justify-between text-sm text-emerald-400">
                  <span className="mono-label !text-emerald-400">Discount{cart.discount.code ? ` · ${cart.discount.code}` : ''}</span>
                  <span className="tabular-nums">−{cart.discount.amount}</span>
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                <span className="mono-label">Total</span>
                <span className="text-2xl font-semibold tabular-nums">{cart.total}</span>
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:justify-end">
              <Link href="/packs" className="inline-grid place-items-center h-12 px-6 border border-line text-sm hover:border-line-2 transition order-2 sm:order-1">
                Keep browsing
              </Link>
              <Link
                href={auth.user ? '/checkout' : '/login?return_to=%2Fcheckout'}
                className="inline-grid place-items-center h-12 px-8 bg-paper text-ink text-sm font-semibold hover:opacity-90 transition order-1 sm:order-2"
              >
                {auth.user ? 'Proceed to checkout →' : 'Sign in to checkout →'}
              </Link>
            </div>
          </>
        )}
      </div>
    </>
  )
}
